/**
 * ReportBuilder — éditeur d'un rapport (builder de widgets) pour Orion (Phase 2a).
 *
 * Assemble les composants de `features/reports/` : PageTabs (onglets pages),
 * PageCanvas (grille drag/drop), WidgetPalette (catalogue widgets) et
 * WidgetConfigDrawer (config du widget sélectionné). Le layout de la page
 * active est sauvegardé côté backend en debounce (~600ms) à chaque
 * changement, pour éviter de spammer l'API pendant un drag continu.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Camera, Check, Eye, LayoutTemplate, Palette, Pencil } from 'lucide-react'
import PageTabs, { type Page } from '../features/reports/PageTabs'
import PageCanvas from '../features/reports/PageCanvas'
import WidgetPalette from '../features/reports/WidgetPalette'
import WidgetConfigDrawer from '../features/reports/WidgetConfigDrawer'
import PeriodPicker from '../features/reports/PeriodPicker'
import { ThemeSelect } from '../features/reportThemes/ThemeSelect'
import { ThemeCover } from '../features/reportThemes/ThemeCover'
import { ThemeManagerDrawer } from '../features/reportThemes/ThemeManagerDrawer'
import { saveReportAsTemplate, saveFromReport } from '../features/reports/reportTemplatesApi'
import { getTheme } from '../features/reportThemes/reportThemesApi'
import { CreateSnapshotModal } from '../features/snapshots/CreateSnapshotModal'
import type { Layout, WidgetType, WidgetConfig, PeriodValue } from '../features/reports/schemas'
import type { MapView } from '../features/reports/widgets/mapLayers'
import type { ReportTheme } from '../types/reportTheme'
import type { Snapshot } from '../types/snapshot'
import { apiPatch } from '../lib/auth'

interface ReportPageFull extends Page {
  layout: Layout
}

interface ReportFull {
  id: number
  projectId: number
  name: string
  defaultPeriod?: unknown
  themeId?: number | null
  isTemplate?: boolean
  templateId?: number | null
  pages: ReportPageFull[]
}

function defaultPeriod(): PeriodValue {
  // 30 jours finissant HIER (convention GA4 : aujourd'hui incomplet exclu).
  const end = new Date()
  end.setDate(end.getDate() - 1)
  const start = new Date()
  start.setDate(start.getDate() - 30)
  const toIso = (d: Date) => d.toISOString().slice(0, 10)
  return { start: toIso(start), end: toIso(end), mode: 'duration', compare: null }
}

/** Un defaultPeriod stocké n'est exploitable que s'il a des dates concrètes (les anciens rapports
 * ont la forme `{mode, range}` sans start/end → on retombe sur les 30 derniers jours). */
function toPeriodValue(stored: unknown): PeriodValue {
  if (
    stored && typeof stored === 'object' &&
    typeof (stored as any).start === 'string' && (stored as any).start &&
    typeof (stored as any).end === 'string' && (stored as any).end
  ) {
    const s = stored as any
    return {
      start: s.start,
      end: s.end,
      mode: s.mode === 'previous_month' ? 'previous_month' : 'duration',
      compare: s.compare ?? null,
    }
  }
  return defaultPeriod()
}

export default function ReportBuilder() {
  const { projectId, reportId } = useParams<{ projectId: string; reportId: string }>()
  const navigate = useNavigate()
  const clientId = Number(projectId)
  const reportIdNum = Number(reportId)

  const [report, setReport] = useState<ReportFull | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [activePageId, setActivePageId] = useState<number | null>(null)
  const [layout, setLayout] = useState<Layout>({ widgets: [] })
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null)
  // Ouverture du drawer de config découplée de la sélection : le drawer ne s'ouvre
  // que sur le crayon (setEditingWidgetId), pas en sélectionnant un widget.
  const [editingWidgetId, setEditingWidgetId] = useState<string | null>(null)
  const [draggingType, setDraggingType] = useState<WidgetType | null>(null)
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [period, setPeriod] = useState<PeriodValue>(() => defaultPeriod())
  const [saveError, setSaveError] = useState<string | null>(null)
  const [showSnapshotModal, setShowSnapshotModal] = useState(false)
  const [activeTheme, setActiveTheme] = useState<ReportTheme | null>(null)
  const [themesDrawerOpen, setThemesDrawerOpen] = useState(false)
  const [savedAsTemplate, setSavedAsTemplate] = useState(false)

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSave = useRef<{ pageId: number; layout: Layout } | null>(null)
  const activePageIdRef = useRef<number | null>(null)
  activePageIdRef.current = activePageId

  const fetchReport = useCallback(
    async (preserveActivePage = true) => {
      if (!clientId || !reportIdNum) return
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(`/api/reporting/clients/${clientId}/reports/${reportIdNum}`, {
          credentials: 'include',
        })
        const body = await res.json().catch(() => ({}))
        if (!res.ok || body?.error) {
          throw new Error(body?.error || `Erreur ${res.status}`)
        }
        const data: ReportFull = body.data
        setReport(data)
        if (!preserveActivePage) setPeriod(toPeriodValue(data.defaultPeriod))
        const sorted = [...data.pages].sort((a, b) => a.position - b.position)
        const keepId = preserveActivePage ? activePageIdRef.current : null
        const nextActive = (keepId && sorted.find((p) => p.id === keepId)) ? keepId : sorted[0]?.id ?? null
        setActivePageId(nextActive)
        const activePage = sorted.find((p) => p.id === nextActive)
        setLayout(activePage?.layout ?? { widgets: [] })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement du rapport')
      } finally {
        setLoading(false)
      }
    },
    [clientId, reportIdNum],
  )

  useEffect(() => {
    fetchReport(false)
  }, [fetchReport])

  async function persistNow(pageId: number, nextLayout: Layout) {
    try {
      const res = await apiPatch(`/api/reporting/clients/${clientId}/reports/${reportIdNum}/pages/${pageId}`, {
        layout: nextLayout,
      })
      setSaveError(res.error ?? null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur réseau')
    }
  }

  function schedulePersist(pageId: number, nextLayout: Layout) {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    pendingSave.current = { pageId, layout: nextLayout }
    saveTimer.current = setTimeout(() => {
      const pending = pendingSave.current
      pendingSave.current = null
      saveTimer.current = null
      if (pending) void persistNow(pending.pageId, pending.layout)
    }, 600)
  }

  // Écrit immédiatement toute sauvegarde en attente (avant changement de page / démontage /
  // rechargement du rapport) pour ne pas perdre la dernière modif faite dans la fenêtre de
  // debounce. Rend une promesse : les appelants qui vont RELIRE le serveur juste après
  // (fetchReport) doivent attendre l'écriture, sinon ils rechargent l'ancien layout.
  function flushPersist(): Promise<void> {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    const pending = pendingSave.current
    pendingSave.current = null
    if (pending) return persistNow(pending.pageId, pending.layout)
    return Promise.resolve()
  }

  function handleLayoutChange(next: Layout) {
    setLayout(next)
    if (activePageId) schedulePersist(activePageId, next)
  }

  function handleSelectPage(pageId: number) {
    void flushPersist()
    setSelectedWidgetId(null)
    setEditingWidgetId(null)
    // Fige le layout courant de la page SORTANTE dans `report` avant de basculer.
    // `report` est la source de vérité de la navigation (handleSelectPage relit
    // `report.pages`), mais il n'est mis à jour qu'au (re)chargement du rapport —
    // JAMAIS après une édition (handleLayoutChange/handleWidgetConfigChange ne
    // touchent que `layout`). Sans ce merge, revenir sur une page recharge son
    // layout initial et perd toutes les modifs de session (symptôme le plus visible :
    // une image ajoutée réapparaît « Aucune image » après un aller-retour).
    const mergedPages = (report?.pages ?? []).map((p) =>
      p.id === activePageId ? { ...p, layout } : p,
    )
    setReport((prev) => (prev ? { ...prev, pages: mergedPages } : prev))
    setActivePageId(pageId)
    const target = mergedPages.find((p) => p.id === pageId)
    setLayout(target?.layout ?? { widgets: [] })
  }

  /**
   * Ajout / suppression / réordonnancement d'une page → on relit le rapport. Il faut ATTENDRE
   * l'écriture d'une éventuelle sauvegarde en attente : sinon `fetchReport` recharge le layout
   * serveur (l'ancien) et écrase l'état React, la modif en cours de debounce est perdue —
   * et la modif suivante repartirait de l'ancien layout.
   */
  async function handlePagesChanged() {
    await flushPersist()
    await fetchReport(true)
  }

  function handleWidgetConfigChange(widgetId: string, config: WidgetConfig) {
    const next: Layout = {
      widgets: layout.widgets.map((w) => (w.id === widgetId ? { ...w, config } : w)),
    }
    setLayout(next)
    if (activePageId) schedulePersist(activePageId, next)
  }

  /**
   * Texte riche édité inline dans le canvas : on réutilise le chemin de persistance
   * des configs (setLayout + debounce), le contenu vivant dans `display.content_html`.
   */
  function handleWidgetContentChange(widgetId: string, html: string) {
    const widget = layout.widgets.find((w) => w.id === widgetId)
    if (!widget) return
    handleWidgetConfigChange(widgetId, {
      ...widget.config,
      display: { ...(widget.config.display ?? {}), content_html: html },
    })
  }

  /**
   * Vue figée d'un widget `map` (bouton « Figer »/« Réinitialiser ») : même chemin de
   * persistance que le texte riche, le bouton stocke dans `display.map_view`.
   */
  function handleWidgetViewChange(widgetId: string, view: MapView | null) {
    const widget = layout.widgets.find((w) => w.id === widgetId)
    if (!widget) return
    const nextDisplay = { ...(widget.config.display ?? {}) }
    if (view) nextDisplay.map_view = view
    else delete nextDisplay.map_view
    handleWidgetConfigChange(widgetId, { ...widget.config, display: nextDisplay })
  }

  async function handlePeriodChange(next: PeriodValue) {
    setPeriod(next)
    // Action délibérée de l'utilisateur → persiste tout de suite (pas de debounce).
    // Même traitement que `persistNow` : un échec doit être VISIBLE, pas avalé.
    try {
      const res = await apiPatch(`/api/reporting/clients/${clientId}/reports/${reportIdNum}`, {
        defaultPeriod: next,
      })
      setSaveError(res.error ?? null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur réseau')
    }
  }

  // Thème du rapport : persiste immédiatement (comme la période). L'appartenance
  // du thème (userId) est re-vérifiée côté backend.
  async function handleThemeChange(themeId: number | null) {
    setReport((r) => (r ? { ...r, themeId } : r))
    try {
      const res = await apiPatch(`/api/reporting/clients/${clientId}/reports/${reportIdNum}`, { themeId })
      setSaveError(res.error ?? null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur réseau')
    }
  }

  async function handleSaveAsTemplate() {
    const name = window.prompt('Nom du template')
    if (!name || !name.trim()) return
    try {
      await saveReportAsTemplate(reportIdNum, name.trim())
      setSavedAsTemplate(true)
      window.setTimeout(() => setSavedAsTemplate(false), 2500)
    } catch {
      // erreur silencieuse : pas de crash du builder
    }
  }

  async function handleSaveToTemplate() {
    if (!report?.templateId) return
    await saveFromReport(report.templateId, reportIdNum)
    navigate('/report-templates')
  }

  async function handleExitTemplateEdit() {
    await fetch(`/api/reporting/clients/${clientId}/reports/${reportIdNum}`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => {})
    navigate('/report-templates')
  }

  // Thème actif du rapport : chargé dès que `themeId` est défini (pas seulement en
  // preview) — pilote la palette de séries des charts (édition + preview) et la
  // page de garde en aperçu. Silencieux en cas d'échec (non bloquant).
  useEffect(() => {
    if (!report?.themeId) {
      setActiveTheme(null)
      return
    }
    let cancelled = false
    getTheme(report.themeId)
      .then((theme) => {
        if (!cancelled) setActiveTheme(theme)
      })
      .catch(() => {
        if (!cancelled) setActiveTheme(null)
      })
    return () => {
      cancelled = true
    }
  }, [report?.themeId])

  const editingWidget = layout.widgets.find((w) => w.id === editingWidgetId) ?? null

  useEffect(() => {
    return () => {
      void flushPersist()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && !report) {
    return (
      <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
        Erreur : {error}
      </div>
    )
  }

  if (!report) return null

  // La couverture (aperçu) est figée à ~960px de haut : sur un viewport plus bas, elle
  // dépasse à elle seule la hauteur disponible du builder. Le corps (canvas) est un flex
  // item `min-h-0`/`overflow-hidden` qui, dans ce cas, se retrouve écrasé à 0px par le
  // calcul flex (rien à distribuer) au lieu de simplement passer sous la couverture — les
  // deux propriétés forcent une taille minimale automatique de 0 (règle CSS des conteneurs
  // à overflow non-visible). Avec couverture, on repasse donc en scroll naturel de toute
  // la colonne (racine overflow-y-auto, corps à sa taille de contenu) plutôt qu'en scroll
  // interne borné — comportement inchangé sans couverture (édition ou aperçu sans cover).
  const showCover = mode === 'preview' && !!activeTheme && !!(activeTheme.cover as { enabled?: boolean })?.enabled

  return (
    <div className={`flex h-full flex-col ${showCover ? 'overflow-y-auto' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#E2E4ED] bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/p/${projectId}/reports`}
            className="flex items-center gap-1.5 text-sm text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.25} />
            Rapports
          </Link>
          <span className="text-[#E2E4ED]">/</span>
          <h1 className="text-sm font-semibold text-[#1B2559]">{report.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Contexte temporel */}
          <PeriodPicker value={period} onChange={handlePeriodChange} />

          <div className="mx-0.5 h-6 w-px bg-[#E2E4ED]" />

          {/* Apparence : thème + gestion des thèmes */}
          <ThemeSelect value={report.themeId ?? null} onChange={handleThemeChange} label="Thème" hideLabel />
          <button
            type="button"
            aria-label="Gérer les thèmes"
            onClick={() => setThemesDrawerOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E4ED] text-[#6366F1] transition-colors hover:border-[#6366F1] hover:bg-[#F4F5FA]"
          >
            <Palette size={16} strokeWidth={1.75} />
          </button>

          <div className="mx-0.5 h-6 w-px bg-[#E2E4ED]" />

          {/* Actions */}
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'edit' ? 'preview' : 'edit'))}
            className="flex items-center gap-1.5 rounded-lg border border-[#E2E4ED] px-3 py-1.5 text-sm text-[#1B2559] transition-colors hover:border-[#6366F1] hover:bg-[#F4F5FA]"
          >
            {mode === 'edit' ? (
              <>
                <Eye className="h-4 w-4" strokeWidth={1.25} />
                Aperçu
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4" strokeWidth={1.25} />
                Édition
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleSaveAsTemplate}
            aria-label={savedAsTemplate ? 'Template enregistré' : 'Enregistrer comme template'}
            className={[
              'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
              savedAsTemplate
                ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                : 'border-[#E2E4ED] text-[#1B2559] hover:border-[#6366F1] hover:bg-[#F4F5FA]',
            ].join(' ')}
          >
            {savedAsTemplate ? (
              <Check className="h-4 w-4" strokeWidth={2} />
            ) : (
              <LayoutTemplate className="h-4 w-4" strokeWidth={1.25} />
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowSnapshotModal(true)}
            className="flex items-center gap-1.5 rounded-lg bg-[#6366F1] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#5558E6]"
          >
            <Camera className="h-4 w-4" strokeWidth={1.5} />
            Snapshot
          </button>
        </div>
      </div>

      {report.isTemplate && (
        <div className="flex items-center justify-between gap-3 border-b border-[#6366F1]/20 bg-[#6366F1]/5 px-4 py-2">
          <span className="text-sm font-medium text-[#6366F1]">Édition du template · aperçu non enregistré comme rapport</span>
          <div className="flex items-center gap-2">
            <button onClick={handleExitTemplateEdit}
              className="rounded-lg border border-[#E2E4ED] px-3 py-1.5 text-sm text-[#1B2559] hover:border-[#6366F1] hover:bg-white">Quitter</button>
            <button onClick={handleSaveToTemplate}
              className="rounded-lg bg-[#6366F1] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#5457E5]">Enregistrer dans le template</button>
          </div>
        </div>
      )}

      {/* Page tabs */}
      <PageTabs
        clientId={clientId}
        reportId={reportIdNum}
        pages={report.pages}
        activePageId={activePageId}
        readOnly={mode === 'preview'}
        onSelectPage={handleSelectPage}
        onPagesChanged={handlePagesChanged}
      />

      {saveError && (
        <div
          role="alert"
          className="mx-4 mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          Sauvegarde impossible : {saveError}
        </div>
      )}

      {mode === 'preview' && activeTheme && (activeTheme.cover as { enabled?: boolean })?.enabled ? (
        <div className="flex justify-center bg-[#F4F5FA] px-4 py-6">
          {/* ThemeCover est figé 1100×1556 : on le met à l'échelle pour l'aperçu (largeur 680). */}
          <div
            className="overflow-hidden rounded-xl shadow-lg"
            style={{ width: 680, height: 680 * (1556 / 1100) }}
          >
            <div style={{ transform: `scale(${680 / 1100})`, transformOrigin: 'top left' }}>
              <ThemeCover
                theme={activeTheme}
                clientLabel={report.name}
                periodLabel={`${period.start} – ${period.end}`}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Body: palette + canvas + config drawer */}
      <div className={`flex flex-1 ${showCover ? '' : 'min-h-0 overflow-hidden'}`}>
        {/* Pas de reset de draggingType sur dragEnd : dans le fork react-grid-layout,
            onDrop est différé et lirait un draggingType déjà remis à null → le widget
            n'était jamais ajouté. On laisse draggingType (écrasé au prochain dragStart),
            comme Metrix. */}
        {mode === 'edit' && (
          <WidgetPalette onDragStartWidget={setDraggingType} />
        )}

        <PageCanvas
          layout={layout}
          mode={mode}
          period={period}
          selectedWidgetId={selectedWidgetId}
          draggingType={draggingType}
          theme={activeTheme}
          onLayoutChange={handleLayoutChange}
          onSelectWidget={setSelectedWidgetId}
          onEditWidget={setEditingWidgetId}
          onWidgetContentChange={handleWidgetContentChange}
          onWidgetViewChange={handleWidgetViewChange}
        />

        {mode === 'edit' && editingWidget && (
          <WidgetConfigDrawer
            clientId={clientId}
            widget={editingWidget}
            onChange={handleWidgetConfigChange}
            onClose={() => setEditingWidgetId(null)}
          />
        )}
      </div>

      {showSnapshotModal && report && (
        <CreateSnapshotModal
          clientId={clientId}
          reportId={reportIdNum}
          defaultPeriod={period}
          defaultName={`Snapshot du ${new Date().toLocaleDateString('fr-FR')}`}
          onClose={() => setShowSnapshotModal(false)}
          onCreated={(snap: Snapshot) => navigate(`/snapshots/${snap.id}`)}
        />
      )}

      <ThemeManagerDrawer
        open={themesDrawerOpen}
        onClose={() => setThemesDrawerOpen(false)}
        activeThemeId={report?.themeId ?? null}
        onAssign={(id) => handleThemeChange(id)}
      />
    </div>
  )
}
